import { useQuery } from '@apollo/client/react';
import { useFocusEffect } from 'expo-router';
import { useCallback } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';

import { SettingsSubHeader } from '@/components/organisms/settings/SettingsSubHeader';
import { formatMeetingSummaryQuotaResetDate } from '@/formatters/formatMeetingSummaryQuotaUsage';
import { MeetingSummaryQuotaUsageDocument } from '@/generated/usermobile-bff/graphql';
import { useTranslation } from '@/i18n';
import { spacing, unisonColors } from '@/theme';

interface QuotaUsageSettingsProps {
  readonly onBack: () => void;
}

interface QuotaBarProps {
  readonly label: string;
  readonly usedMinutes: number;
  readonly allowanceMinutes: number;
  readonly resetDateText: string;
}

const progressBarHeight = 6;
const progressTrackColor = '#ebf3f5';

const QuotaBar = ({ label, usedMinutes, allowanceMinutes, resetDateText }: QuotaBarProps) => {
  const { t } = useTranslation();
  const exceeded = allowanceMinutes > 0 && usedMinutes >= allowanceMinutes;
  const progressValue = allowanceMinutes > 0 ? Math.min(1, usedMinutes / allowanceMinutes) : 0;
  const progressColor = exceeded ? unisonColors.error : unisonColors.brandGreenStrong;

  return (
    <View style={styles.card}>
      {exceeded ? (
        <Text variant="bodySmall" style={styles.resetSubtitle}>
          {resetDateText}
        </Text>
      ) : null}
      <View style={styles.cardHeader}>
        <Text variant="bodyLarge" style={styles.cardTitle}>
          {label}
        </Text>
        <Text variant="bodyLarge" style={styles.usageValue}>
          {Math.min(usedMinutes, allowanceMinutes)}/{allowanceMinutes}
          {t('settings.quotaUsage.minutesSuffix')}
        </Text>
      </View>
      <View style={styles.progressTrack}>
        <View
          style={[
            styles.progressFill,
            { width: `${progressValue * 100}%`, backgroundColor: progressColor },
          ]}
        />
      </View>
    </View>
  );
};

export const QuotaUsageSettings = ({ onBack }: QuotaUsageSettingsProps) => {
  const { t, locale } = useTranslation();
  const { data, loading, refetch } = useQuery(MeetingSummaryQuotaUsageDocument, {
    fetchPolicy: 'cache-and-network',
  });

  useFocusEffect(
    useCallback(() => {
      void refetch();
    }, [refetch]),
  );

  const quotas = data?.meetingSummaryQuotaUsage.quotas ?? [];
  const userQuota = quotas.find((q) => q.type === 'USER');
  const accountQuota = quotas.find((q) => q.type === 'ACCOUNT');
  const resetDateText = formatMeetingSummaryQuotaResetDate({ locale, t });

  return (
    <>
      <SettingsSubHeader title={t('settings.quotaUsage.screenTitle')} onBack={onBack} />
      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        <Text variant="bodyLarge" style={styles.description}>
          {t('settings.quotaUsage.description')}
        </Text>
        {loading && !data ? (
          <ActivityIndicator size="small" style={styles.loader} />
        ) : (
          <View style={styles.sections}>
            <View style={styles.section}>
              <View style={styles.sectionTextGroup}>
                <Text variant="labelLarge" style={styles.sectionTitle}>
                  {t('settings.quotaUsage.personalQuotaTitle')}
                </Text>
                <Text variant="bodySmall" style={styles.sectionDescription}>
                  {t('settings.quotaUsage.personalQuotaDescription')}
                </Text>
              </View>
              <QuotaBar
                label={t('settings.quotaUsage.personalQuotaLabel')}
                usedMinutes={userQuota?.usedMinutes ?? 0}
                allowanceMinutes={userQuota?.allowanceMinutes ?? 0}
                resetDateText={resetDateText}
              />
            </View>
            <View style={styles.section}>
              <View style={styles.sectionTextGroup}>
                <Text variant="labelLarge" style={styles.sectionTitle}>
                  {t('settings.quotaUsage.companyQuotaTitle')}
                </Text>
                <Text variant="bodySmall" style={styles.sectionDescription}>
                  {t('settings.quotaUsage.companyQuotaDescription')}
                </Text>
              </View>
              <QuotaBar
                label={t('settings.quotaUsage.companyQuotaLabel')}
                usedMinutes={accountQuota?.usedMinutes ?? 0}
                allowanceMinutes={accountQuota?.allowanceMinutes ?? 0}
                resetDateText={resetDateText}
              />
            </View>
          </View>
        )}
      </ScrollView>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: unisonColors.bgNlWeak,
  },
  contentContainer: {
    paddingHorizontal: spacing[4],
    paddingTop: spacing[4],
    gap: spacing[4],
  },
  description: {
    color: unisonColors.textNdPrimary,
  },
  sections: {
    gap: spacing[4],
  },
  section: {
    gap: spacing[2],
  },
  sectionTextGroup: {
    gap: 0,
  },
  sectionTitle: {
    color: unisonColors.textNdPrimary,
    fontWeight: 'bold',
    fontSize: 14,
    lineHeight: 24,
  },
  sectionDescription: {
    color: unisonColors.textNdPrimary,
    fontSize: 12,
    lineHeight: 24,
  },
  card: {
    backgroundColor: unisonColors.white,
    borderRadius: 8,
    overflow: 'hidden',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 24,
    gap: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  resetSubtitle: {
    color: unisonColors.textNdSecondary,
  },
  cardTitle: {
    color: unisonColors.textNdPrimary,
    flexShrink: 1,
    flexGrow: 1,
    marginRight: 8,
  },
  usageValue: {
    color: unisonColors.textNdPrimary,
    flexShrink: 0,
  },
  loader: {
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  progressTrack: {
    height: progressBarHeight,
    backgroundColor: progressTrackColor,
    borderRadius: progressBarHeight / 2,
    overflow: 'hidden',
    marginTop: 4,
  },
  progressFill: {
    height: progressBarHeight,
    borderRadius: progressBarHeight / 2,
  },
});
